import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { loadCartUpsell } from './cart-drawer-upsell.js';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('cart-drawer:open', this.#handleOpenRequest);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('cart-drawer:open', this.#handleOpenRequest);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #handleOpenRequest = () => {
    this.showDialog();
  };

  showDialog() {
    this.#syncEmptyState();
    super.showDialog();
    loadCartUpsell();
  }

  /**
   * Defensive safety net: ensures the dialog's `cart-drawer--empty` class
   * reflects what's actually rendered inside it before it becomes visible.
   * DOM morphing on cart updates only patches matched descendant nodes, so a
   * stale `cart-drawer--empty` class can otherwise linger on the dialog after
   * the cart goes from empty to non-empty, breaking the header/footer layout.
   */
  #syncEmptyState() {
    const dialog = this.querySelector('dialog.cart-drawer__dialog');
    if (!dialog) return;

    const isEmpty = !dialog.querySelector('#cart-drawer-heading');
    dialog.classList.toggle('cart-drawer--empty', isEmpty);
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
